import { useToast } from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEditEventdetector, useShapeFetch } from '@ais-ui/api';
import { useGetFilters, useGetShareGroups } from '@ais-ui/websockets';
import type { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { useLocalization } from '@ais-ui/locale-management';

import { grabEventDetectorInitialGroups } from '../../../Groups/grabInitialGroups';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import {
  LineDetectorFormValues,
  lineDetectorFormSchema,
  convertFormValuesToBackend,
} from './lineForm';
import { LineDetectorDialog } from './LineDetectorDialog';

interface EditDetectorDialogProps {
  detector: EventdetectorDefinition;
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
}

export const EditLineDetectorDialog = ({
  detector,
  showDialog,
  setShowDialog,
}: EditDetectorDialogProps) => {
  const { messages } = useLocalization();
  const { mutate } = useEditEventdetector();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formInitialized, setFormInitialized] = React.useState(false);
  const { toast } = useToast();
  const shapeFetch = useShapeFetch();

  const { data: filters } = useGetFilters();
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();

  const form = useForm<LineDetectorFormValues>({
    resolver: zodResolver(lineDetectorFormSchema),
    defaultValues: {
      name: detector.name,
      enabled: detector.enabled,
      shapeId: detector.shape,
      selectedGroups: grabEventDetectorInitialGroups(detector, groups),
      filterId: detector.filterId ? detector.filterId : 'no-filter',
      activationText: detector.activationText,
      deactivationText: detector.deactivationText,
      directionMinAngle: detector.checkDirection?.minAngle
        ? detector.checkDirection.minAngle
        : undefined,
      directionMaxAngle: detector.checkDirection?.maxAngle
        ? detector.checkDirection.maxAngle
        : undefined,
      timeBeforeActivation: detector.timeBeforeActivation
        ? detector.timeBeforeActivation
        : undefined,
      timeBeforeDeactivation: detector.timeBeforeDeactivation
        ? detector.timeBeforeDeactivation
        : undefined,
      timeBetweenTrigger: detector.timeBetweenTriggers
        ? detector.timeBetweenTriggers
        : undefined,
      timeoutWhenActive: detector.timeoutWhenActive
        ? detector.timeoutWhenActive
        : undefined,
      severity: detector.severity,
      sendShipNotification: detector.sendShipNotification,
      shipNotificationText: detector.shipNotificationText,
      acknowledgeRequired: detector.ackRequired,
      maintainShiplist: detector.shiplist?.active
        ? detector.shiplist.active
        : false,
      navigationStatusTriggerThreshold: detector.navStatusTriggerThreshold,
      navigationStatusIgnoreThreshold: detector.navStatusIgnoreThreshold,
      driftingSpeedOverGroundThreshold: detector.driftingSogThreshold,
      driftingAngleThreshold: detector.driftingChdThreshold,
      driftingTimeThreshold: detector.driftingTimeThreshold,
      exitCheckThreshold: detector.exitCheckThreshold,
      exitCheckTriggerLost: detector.exitCheckTriggerLost,
      deltaSpeedOverGroundThreshold: detector.deltaSogThreshold,
      useVesselDimensions: detector.useVesselDimensions,
      emails: detector.notify
        ? detector.notify
            .filter((notifier) => notifier.action === 'email')
            .map((notifier) => notifier.who)
        : [],
      phones: detector.notify
        ? detector.notify
            .filter((notifier) => notifier.action === 'sms')
            .map((notifier) => notifier.who)
        : [],
    },
  });

  useEffect(() => {
    if (!isLoadingGroups && !formInitialized) {
      form.reset({
        ...form.getValues(), // Preserve other values
        selectedGroups: grabEventDetectorInitialGroups(detector, groups),
      });
      setFormInitialized(true); // Prevent re-initialization
    }
  }, [isLoadingGroups, groups, form, formInitialized, detector]);

  const onEdit = useCallback(
    async (data: LineDetectorFormValues) => {
      if (!filters) {
        return;
      }

      let shape = null;
      if (data.shapeId) {
        shape = await shapeFetch(data.shapeId);
      }

      if (!shape) {
        return;
      }

      const filter = filters.find((filter) => filter.uuid === data.filterId);

      const backendValues = convertFormValuesToBackend(
        detector.type,
        data,
        shape,
        filter
      );

      setIsSubmitting(true);

      const callBacks = mutateCallbacks(
        toast,
        messages,
        'edit',
        'detector',
        setIsSubmitting,
        setShowDialog
      );

      mutate(
        {
          id: detector.id as string,
          eventdetectorDefinition: backendValues,
        },
        callBacks
      );
    },
    [filters, shapeFetch, mutate, detector.type, detector.id]
  );

  return (
    <LineDetectorDialog
      form={form}
      onSubmit={onEdit}
      setShowOuterDialog={setShowDialog}
      mutateType="edit"
      isSubmitting={isSubmitting}
      detector={detector}
    ></LineDetectorDialog>
  );
};
